import strings from '../../constant/strings';

/**
 * Helpers shared by the pre-order pages.
 *
 * The important one is the intent store. A visitor arriving from a social post
 * has no account, so they will be sent through login or signup part-way
 * through the flow. `PrivateRoute` redirects to "/" and keeps nothing, so what
 * they had chosen - and which ad brought them - is remembered here and picked
 * up again when they come back. Without this the campaign attribution on the
 * order would silently be empty.
 */

const INTENT_KEY = 'emed4u.preorder.intent';

// All money from the API is integer paise.
export const formatPaise = (paise) => {
  const value = (Number(paise) || 0) / 100;
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency', currency: 'INR', maximumFractionDigits: 2, minimumFractionDigits: 0,
    }).format(value);
  } catch (e) {
    return `₹${value.toFixed(2)}`;
  }
};

export const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/** "21 Nov - 06 Dec 2026", the window as a shopper reads it. */
export const formatWindow = (from, to) => {
  if (!from && !to) return '';
  if (!to) return formatDate(from);
  return `${formatDate(from)} - ${formatDate(to)}`;
};

export const PLAN_COPY = {
  PREPAID: { title: strings.payInFull, note: strings.payInFullNote },
  DEPOSIT: { title: strings.payDeposit, note: strings.payDepositNote },
  PAY_LATER: { title: strings.reserveNow, note: strings.reserveNowNote },
};

/** Which legal document each consent's wording comes from. */
export const CONSENT_COPY = {
  account_terms_privacy: {
    label: 'I accept the Terms of Service and the Privacy Notice.',
    documentType: 'terms',
  },
  order_data_processing: {
    label: 'I agree to Emed4u using my contact details and delivery address to fulfil this order.',
    documentType: 'privacy',
  },
  payment_schedule_terms: {
    // Worded for a pre-order that takes no money today. "What I am paying now"
    // is zero, so the old wording acknowledged nothing - and this box is the
    // only record that the patient knew a bill was coming at all.
    label: 'I understand nothing is charged today, and that I must pay in full '
      + 'when my device is ready — within 7 days of being asked, or my reservation is released.',
    documentType: 'preorder_terms',
  },
  transactional_reminders: {
    label: 'Send me order and payment reminders.',
    documentType: 'preorder_terms',
  },
  marketing_messages: {
    label: 'Send me occasional offers and product news.',
    documentType: 'privacy',
  },
  whatsapp_optin: {
    label: 'Contact me on WhatsApp about this order.',
    documentType: 'privacy',
  },
  saved_payment_mandate: {
    label: 'Save my payment method for the remaining instalment.',
    documentType: 'preorder_terms',
  },
  device_health_data_link: {
    label: 'Link readings from this device to my Emed4u health record.',
    documentType: 'privacy',
  },
};

/**
 * Reads the campaign parameters off the landing URL. Captured on first view,
 * because they are gone from the URL by the time checkout finishes.
 */
export const readAttribution = (search, href) => {
  const params = new URLSearchParams(search || '');
  const pick = (...keys) => {
    for (const key of keys) {
      const value = params.get(key);
      if (value) return value.slice(0, 200);
    }
    return '';
  };
  return {
    source: pick('utm_source', 'source', 'ref'),
    medium: pick('utm_medium', 'medium'),
    campaign: pick('utm_campaign', 'campaign'),
    landingUrl: String(href || '').slice(0, 500),
  };
};

/** Remembers the choice across a trip through login or signup. */
export const saveIntent = (intent) => {
  try {
    sessionStorage.setItem(INTENT_KEY, JSON.stringify({ ...intent, at: Date.now() }));
  } catch (e) { /* private mode; the flow still works, attribution is just lost */ }
};

export const readIntent = () => {
  try {
    const raw = sessionStorage.getItem(INTENT_KEY);
    if (!raw) return null;
    const intent = JSON.parse(raw);
    // An hour is long enough to sign up, short enough that a stale choice from
    // a previous visit does not reappear.
    if (!intent.at || Date.now() - intent.at > 60 * 60 * 1000) {
      sessionStorage.removeItem(INTENT_KEY);
      return null;
    }
    return intent;
  } catch (e) {
    return null;
  }
};

const PLACED_KEY = 'emed4u.preorder.justPlaced';

/**
 * Carries "the order went through" across the navigation that follows, so the
 * confirmation can be shown on the page the patient actually lands on rather
 * than flashed on the one being left behind.
 *
 * Written only when the server says an order exists, so it can never claim a
 * success that did not happen.
 */
export const markJustPlaced = (orderNumber) => {
  try {
    sessionStorage.setItem(PLACED_KEY, JSON.stringify({ orderNumber, at: Date.now() }));
  } catch (e) { /* private mode: they still land on the list, just unannounced */ }
};

/** Reads it once and clears it, so a reload does not re-announce the order. */
export const takeJustPlaced = () => {
  try {
    const raw = sessionStorage.getItem(PLACED_KEY);
    sessionStorage.removeItem(PLACED_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    // A stale flag from an abandoned tab should not greet them later.
    if (!v.at || Date.now() - v.at > 5 * 60 * 1000) return null;
    return v;
  } catch (e) {
    return null;
  }
};

const DRAFT_KEY = 'emed4u.preorder.draft';

/**
 * The address and consents a visitor filled in before they had an account.
 *
 * Kept here only to carry them across the sign-in step in this browser; the
 * copy that actually matters is sent with the signup and stored server-side,
 * because the patient may well activate from a different device.
 */
export const saveDraft = (draft) => {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ ...draft, at: Date.now() }));
  } catch (e) { /* private mode; they will just re-enter it */ }
};

export const readDraft = () => {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw);
    if (!draft.at || Date.now() - draft.at > 60 * 60 * 1000) {
      sessionStorage.removeItem(DRAFT_KEY);
      return null;
    }
    return draft;
  } catch (e) {
    return null;
  }
};

export const clearDraft = () => {
  try { sessionStorage.removeItem(DRAFT_KEY); } catch (e) { /* nothing to clear */ }
};

export const clearIntent = () => {
  try { sessionStorage.removeItem(INTENT_KEY); } catch (e) { /* nothing to clear */ }
};

/** Patient-readable wording for the order statuses the API returns. */
const STATUS_LABELS = {
  pending_payment: 'Awaiting payment',
  reserved: 'Reserved',
  confirmed: 'Confirmed',
  awaiting_balance: 'Balance due',
  balance_overdue: 'Balance overdue',
  ready_to_ship: 'Ready to ship',
  shipped: 'Shipped',
  delivered: 'Delivered',
  payment_failed: 'Payment failed',
  expired: 'Expired',
  cancelled_by_user: 'Cancelled',
  cancelled_by_seller: 'Cancelled by Emed4u',
  cancelled_unpaid: 'Cancelled, unpaid',
  refund_pending: 'Refund in progress',
  refunded: 'Refunded',
};

export const statusLabel = (status) => STATUS_LABELS[status]
  || String(status || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Statuses where money is still expected from the patient.
const OWING = ['reserved', 'confirmed', 'awaiting_balance', 'balance_overdue'];

export const canPayNow = (order) => Boolean(order)
  && order.dueAmount > 0
  && OWING.includes(order.status);

/** The next instalment that has not been paid or cancelled. */
export const nextInstalment = (order) => (order?.schedule || [])
  .filter((item) => item.status !== 'paid' && item.status !== 'cancelled')
  .sort((a, b) => a.seq - b.seq)[0] || null;

/**
 * Whether we are actually asking for the money yet.
 *
 * Nothing is collected when a pre-order is placed, so for most of an order's
 * life the patient owes the full amount but is not being chased for it. Until
 * the unit is ready we say when payment will be asked for; once it is due - or
 * once a link has gone out - we ask.
 */
export const isPaymentDue = (order) => {
  if (!canPayNow(order)) return false;
  if (order.status === 'awaiting_balance' || order.status === 'balance_overdue') return true;
  const next = nextInstalment(order);
  if (!next) return false;
  if (next.status === 'link_sent') return true;
  return Boolean(next.dueAt) && new Date(next.dueAt) <= new Date();
};

/** Paying ahead of the request is allowed, just not pushed. */
export const canPayEarly = (order) => canPayNow(order) && !isPaymentDue(order);

export const canCancel = (order) => Boolean(order)
  && ['pending_payment', 'reserved', 'confirmed', 'awaiting_balance', 'balance_overdue'].includes(order.status);

/**
 * Back to the top of the page.
 *
 * `window.scrollTo` alone does nothing on this site. The stylesheet gives
 * <body> the viewport's height and its own scrollbar, so <body> is the
 * element that scrolls and the document never does: measured in the browser,
 * html.scrollHeight is one viewport (813px) while body.scrollHeight is the
 * whole page (7104px), and setting window scroll leaves the page where it
 * was. That is also why the app-wide <ScrollToTop> from
 * react-router-scroll-top, which calls window.scrollTo on every route
 * change, has never moved anything.
 *
 * All four are set because which one is the scroller depends on that CSS:
 * fix the stylesheet one day and the window becomes the scroller again, and
 * this should keep working either way. Setting scrollTop on an element that
 * does not scroll is harmless.
 */
export const scrollPageToTop = () => {
  if (typeof window === 'undefined') return;
  if (typeof window.scrollTo === 'function') window.scrollTo(0, 0);
  [document.scrollingElement, document.documentElement, document.body].forEach((el) => {
    if (el) el.scrollTop = 0;
  });
};
